import React, { useRef, useEffect, useState } from 'react';

const ScratchCard = ({ children, image, coverColor = '#ffc1d5' }) => {
  const canvasRef = useRef(null);
  const lastPointRef = useRef(null);
  const lastRevealCheckRef = useRef(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Set canvas size
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Draw the scratch-off layer
    ctx.fillStyle = coverColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add text on the scratch layer
    ctx.fillStyle = '#d81b60';
    ctx.font = 'italic 24px "Dancing Script", cursive';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Gratta e Scopri', canvas.width / 2, canvas.height / 2);
  }, [coverColor]);

  const eraseBrush = (ctx, x, y) => {
    const speckCount = 8;
    for (let i = 0; i < speckCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * 12;
      const offsetX = Math.cos(angle) * distance;
      const offsetY = Math.sin(angle) * distance;
      const radius = 4 + Math.random() * 6;
      ctx.globalAlpha = 0.3 + Math.random() * 0.45;
      ctx.beginPath();
      ctx.arc(x + offsetX, y + offsetY, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  };

  const scratchTo = (x, y) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    // Calculate position relative to canvas
    const canvasX = x - rect.left;
    const canvasY = y - rect.top;

    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = '#000000';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const lastPoint = lastPointRef.current;
    if (lastPoint) {
      const dx = canvasX - lastPoint.x;
      const dy = canvasY - lastPoint.y;
      const distance = Math.hypot(dx, dy);

      ctx.globalAlpha = 0.95;
      ctx.lineWidth = 34;
      ctx.beginPath();
      ctx.moveTo(lastPoint.x, lastPoint.y);
      ctx.lineTo(canvasX, canvasY);
      ctx.stroke();

      const steps = Math.max(1, Math.floor(distance / 6));
      for (let step = 0; step <= steps; step++) {
        const t = step / steps;
        const px = lastPoint.x + dx * t;
        const py = lastPoint.y + dy * t;
        eraseBrush(ctx, px, py);
      }
    } else {
      eraseBrush(ctx, canvasX, canvasY);
    }

    lastPointRef.current = { x: canvasX, y: canvasY };
    ctx.restore();

    // Check if enough has been scratched
    checkRevealPercentage();
  };

  const checkRevealPercentage = (force = false) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const now = Date.now();
    if (!force && now - lastRevealCheckRef.current < 120) return;
    lastRevealCheckRef.current = now;
    const ctx = canvas.getContext('2d');
    if (canvas.width === 0 || canvas.height === 0) return;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;

    let transparent = 0;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] < 128) transparent++;
    }

    const percentage = (transparent / (pixels.length / 4)) * 100;
    if (percentage > 65 && !isRevealed) {
      setIsRevealed(true);
      // Clear the entire canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  // Mouse events
  const handleMouseDown = (e) => {
    setIsDrawing(true);
    lastPointRef.current = null;
    scratchTo(e.clientX, e.clientY);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    scratchTo(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    lastPointRef.current = null;
    checkRevealPercentage(true);
  };

  // Touch events
  const handleTouchStart = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    lastPointRef.current = null;
    const touch = e.touches[0];
    scratchTo(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const touch = e.touches[0];
    scratchTo(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    setIsDrawing(false);
    lastPointRef.current = null;
    checkRevealPercentage(true);
  };

  return (
    <div className="scratch-card-container">
      <div className="scratch-card-content">
        {image && (
          <div className="scratch-card-image">
            <img src={image} alt="Activity" />
          </div>
        )}
        {children}
      </div>
      {!isRevealed && (
        <canvas
          ref={canvasRef}
          className="scratch-canvas"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        />
      )}
    </div>
  );
};

export default ScratchCard;
